import { useSDKConfigStore } from "../stores/sdkConfigStore";
import {
  getCreditDelegationTxData,
  getCreditInformation,
  getUserCards,
  getUserConfig,
  initializeUserConfig,
} from "../api/api";
import { useCreditStore } from "../stores/creditStore";
import {
  mapApiTypeToStoreKey,
  CreditProviderId,
  isCreditProviderOfType,
  BASE_CHAIN_ID,
  getCreditProvider,
} from "../config/creditRegistry";
import { UserParams } from "../api/types";

interface DelegateCardParams {
  creditType: CreditProviderId;
  creditLimit: number;
  sendTransactionAsync: (params: {
    to: string;
    value: bigint;
    data: string;
  }) => Promise<string>;
  userParams?: UserParams;
  chainID: number;
}

interface DelegationResult {
  success: boolean;
  message: string;
  transactionHash?: string;
}

export async function delegateAmount({
  creditLimit,
  creditType,
  sendTransactionAsync,
  userParams,
  chainID,
}: DelegateCardParams): Promise<DelegationResult> {
  const { jwt } = useSDKConfigStore.getState();
  const creditStore = useCreditStore.getState();

  // Early validation
  if (!jwt?.eoa) {
    return {
      success: false,
      message: "EOA address not found. Please initialize the SDK first.",
    };
  }

  if (creditLimit < 0) {
    return {
      success: false,
      message: "Credit limit must be equal or greater than 0.",
    };
  }

  try {
    // Step 1: Get transaction data
    console.log(
      `[delegateAmount] Step 1: Fetching delegation data for ${creditType}...`
    );
    const response = await getCreditDelegationTxData({
      from: jwt?.eoa,
      creditLimit,
      creditType,
      userParams,
      chainID,
    });

    const { to, callData, value } = response.data;
    if (!to || !callData) {
      return { success: false, message: "Invalid transaction data received." };
    }

    // Step 2: Execute transaction
    console.log("[delegateAmount] Step 2: Sending delegation transaction...");
    const transactionHash = await sendTransactionAsync({
      to,
      value: BigInt(value || 0),
      data: callData,
    });
    console.log(`[delegateAmount] Transaction sent: ${transactionHash}`);

    // Step 3: Update config and state
    console.log(
      "[delegateAmount] Step 3: Updating config and fetching credit info..."
    );

    const userCardsResponse = await getUserCards();

    // Update card data
    const userCard = userCardsResponse.data?.[0] || null;

    // Validate creditType is a spend mode provider
    if (
      !userCard &&
      userParams &&
      isCreditProviderOfType(creditType, "spend")
    ) {
      await initializeUserConfig(
        chainID,
        creditType,
        userParams as Record<string, string>
      );
    }
    // add refer code
    const userConfig = userCard
      ? {
          solverType: userCard.solverType,
          config: userCard.solverConfig,
        }
      : await getUserConfig();

    creditStore._setUserConfig(userConfig ? userConfig["config"] : null);

    const currentConfig =
      userConfig?.solverType === creditType ? userConfig["config"] : undefined;

    const provider = getCreditProvider(creditType);

    const creditInfo = await getCreditInformation(
      creditType,
      provider?.chainId ?? BASE_CHAIN_ID,
      currentConfig
    );

    creditStore._setCreditInfo(
      mapApiTypeToStoreKey(creditType) as any,
      creditInfo
    );
    creditStore._setLoading(false);

    console.log("[delegateAmount] Delegation completed successfully!");
    return {
      success: true,
      message: "Your collateral approval was successful.",
      transactionHash,
    };
  } catch (error) {
    console.error("[delegateAmount] Error:", error);
    return { success: false, message: "Failed to delegate amount." };
  }
}
